import { Button } from "components/DesignSystem/Button/Button";
import Input from "components/DesignSystem/Input/Input";
import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { TriangleWarning } from "components/icons/TriangleWarning";
import { useToast } from "hooks/useToast";
import { useSaveInstanceAsTemplateMutation } from "store/apis/incorporationFormBuilder";
import { BackendError } from "types/utils/error";

type FormValues = {
  title: string;
};

type SaveAsBaseTemplateModalProps = {
  isOpen: boolean;
  close: () => void;
  formInstanceId: string;
};

export const SaveAsBaseTemplateModal = ({
  isOpen,
  close,
  formInstanceId,
}: SaveAsBaseTemplateModalProps) => {
  const { alertToast, successToast } = useToast();
  const [saveInstanceAsTemplate, { isLoading }] =
    useSaveInstanceAsTemplateMutation();

  const onSubmit = async (values: FormValues) => {
    try {
      await saveInstanceAsTemplate({
        formInstanceId,
        title: values.title,
      }).unwrap();
      successToast({ message: "Saved as a new base template" });
      close();
    } catch (err) {
      alertToast({
        message:
          (err as BackendError)?.data?.error?.message ?? "Something went wrong",
      });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content size="small">
        <FormikForm<FormValues>
          initialValues={{ title: "" }}
          onSubmit={onSubmit}
        >
          {({ values }) => (
            <>
              <Modal.Body>
                <span className="t-text-orange t-flex t-h-10 t-w-10 t-items-center t-justify-center t-rounded-full t-border t-border-solid t-border-orange-20 t-bg-yellow-20 t-shadow-[0px_0px_0px_4px_rgba(255,220,78,0.15)]">
                  <TriangleWarning />
                </span>
                <p className="t-mb-2 t-mt-5 t-text-subtext">
                  Save as a base template?
                </p>
                <div className="t-mb-4 t-text-body-sm t-text-text-30">
                  This checklist will be saved as a reusable base template. Its
                  steps will be available as selectors when creating future
                  incorporation checklists, and you can manage it anytime from
                  the Templates dashboard.
                </div>
                <Input
                  withForm
                  name="title"
                  label="Template name"
                  required
                  placeholder="e.g., LLP → US entity → India subsidiary"
                />
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button type="button">Cancel</Button>
                </Modal.RawClose>
                <Button
                  type="submit"
                  customType="primary"
                  isLoading={isLoading}
                  disabled={isLoading || !values.title}
                >
                  Save as template
                </Button>
              </Modal.FooterButtonGroup>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
